import { Contract } from "ethers";
import {
  extractAuctionState,
  extractListingState,
  getFallbackMetadata,
  normalizeTokenId,
} from "./nftHydration";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const DEFAULT_BATCH_SIZE = 100;

const MULTICALL3_ABI = [
  "function aggregate3((address target,bool allowFailure,bytes callData)[] calls) view returns ((bool success,bytes returnData)[] returnData)",
];

const probeCache = new Map();

function clampToPositiveInt(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.floor(parsed);
}

function normalizeAddress(address) {
  return address ? String(address).toLowerCase() : null;
}

function splitIntoChunks(items, chunkSize) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const chunks = [];
  for (let idx = 0; idx < items.length; idx += chunkSize) {
    chunks.push(items.slice(idx, idx + chunkSize));
  }
  return chunks;
}

function normalizeMulticallResultItem(item) {
  if (Array.isArray(item)) {
    return {
      success: Boolean(item[0]),
      returnData: item[1] || "0x",
    };
  }

  return {
    success: Boolean(item?.success),
    returnData: item?.returnData || "0x",
  };
}

function decodeResult(iface, functionName, returnData) {
  const decoded = iface.decodeFunctionResult(functionName, returnData);
  if (!decoded) return null;
  if (decoded.length === 1) return decoded[0];
  return decoded;
}

function buildDynamicPatchFromReads({
  tokenId,
  owner,
  retirementDetails,
  listingRaw,
  auctionRaw,
  account,
  existingAuctionStartBlock,
}) {
  const normalizedId = normalizeTokenId(tokenId);
  const listing = extractListingState(listingRaw || null);
  const auction = extractAuctionState(auctionRaw || null);

  const retired = Boolean(retirementDetails?.isRetired ?? retirementDetails?.[0] ?? false);
  const retiredBy = retirementDetails?.verifier ?? retirementDetails?.retiredBy ?? retirementDetails?.[1] ?? null;
  const retiredTimestamp = retirementDetails?.timestamp ?? retirementDetails?.[2] ?? null;

  const ownerNormalized = normalizeAddress(owner);
  const accountNormalized = normalizeAddress(account);

  const isAuction = Boolean(auction.active);
  const isListed = Boolean(listing.active || isAuction);
  const auctionHighestBidder = normalizeAddress(auction.highestBidder);

  const price = isAuction
    ? (auction.highestBid && Number(auction.highestBid) > 0 ? auction.highestBid : auction.minPrice)
    : listing.price;

  const seller = isAuction
    ? (auctionHighestBidder && auctionHighestBidder !== ZERO_ADDRESS ? auctionHighestBidder : auction.seller)
    : listing.seller;

  return {
    tokenId: normalizedId,
    owner,
    isOwner: Boolean(accountNormalized && ownerNormalized === accountNormalized),
    retired,
    retiredBy,
    retiredTimestamp,
    isListed,
    isAuction,
    price,
    seller,
    auctionData: isAuction
      ? {
          active: auction.active,
          seller: auction.seller,
          minPrice: auction.minPrice,
          startingPrice: auction.startingPrice,
          highestBid: auction.highestBid,
          highestBidder: auction.highestBidder,
          endTime: auction.endTime,
          auctionStartBlock: existingAuctionStartBlock,
        }
      : {
          active: false,
          seller: null,
          minPrice: null,
          startingPrice: null,
          highestBid: null,
          highestBidder: null,
          endTime: null,
          auctionStartBlock: existingAuctionStartBlock,
        },
    dynamicStatus: "ready",
    lastHydratedBlock: null,
  };
}

export function getMulticallConfig() {
  const enabled = process.env.REACT_APP_MULTICALL_ENABLED === "true";
  const address = String(process.env.REACT_APP_MULTICALL_ADDRESS || "").trim();
  const batchSize = clampToPositiveInt(process.env.REACT_APP_MULTICALL_BATCH_SIZE, DEFAULT_BATCH_SIZE);
  const requireSuccess = process.env.REACT_APP_MULTICALL_REQUIRE_SUCCESS === "true";

  let reason = "configured";
  if (!enabled) reason = "disabled";
  else if (!address) reason = "missing-address";

  return {
    enabled,
    address,
    batchSize,
    requireSuccess,
    configured: enabled && Boolean(address),
    reason,
  };
}

export function isMulticallConfigured() {
  return getMulticallConfig().configured;
}

export async function probeMulticall(provider) {
  const config = getMulticallConfig();
  if (!provider || !config.configured) return false;

  const network = await provider.getNetwork().catch(() => null);
  const chainId = Number(network?.chainId || 0) || 0;
  const cacheKey = `${chainId}:${config.address.toLowerCase()}`;

  if (probeCache.has(cacheKey)) {
    return probeCache.get(cacheKey);
  }

  try {
    const contract = new Contract(config.address, MULTICALL3_ABI, provider);
    await contract.aggregate3([
      {
        target: config.address,
        allowFailure: true,
        callData: "0x",
      },
    ]);
    probeCache.set(cacheKey, true);
    return true;
  } catch {
    probeCache.set(cacheKey, false);
    return false;
  }
}

export async function multicallRead(provider, calls, options = {}) {
  if (!provider) {
    throw new Error("Provider is required for multicallRead");
  }

  const config = getMulticallConfig();
  const multicallAddress = String(options.multicallAddress || config.address || "").trim();
  if (!multicallAddress) {
    throw new Error("Multicall address is not configured");
  }

  const batchSize = clampToPositiveInt(options.batchSize, config.batchSize || DEFAULT_BATCH_SIZE);
  const contract = new Contract(multicallAddress, MULTICALL3_ABI, provider);
  const chunks = splitIntoChunks(calls || [], Math.max(1, batchSize));

  const results = [];
  let aggregateCalls = 0;

  for (const chunk of chunks) {
    aggregateCalls += 1;
    const response = await contract.aggregate3(chunk);
    for (const item of response || []) {
      results.push(normalizeMulticallResultItem(item));
    }
  }

  return {
    results,
    aggregateCalls,
  };
}

export async function hydrateDynamicTokensViaMulticall({
  provider,
  nftContract,
  marketplaceContract,
  nftInterface,
  marketplaceInterface,
  tokenIds,
  userAccount,
  currentState,
}) {
  const config = getMulticallConfig();
  const normalizedIds = (tokenIds || []).map(normalizeTokenId).filter(Boolean);

  const patches = [];
  const failedTokenIds = [];

  if (!normalizedIds.length) {
    return {
      patches,
      failedTokenIds,
      stats: {
        tokenCount: 0,
        lowLevelCalls: 0,
        aggregateCalls: 0,
        partialFailureCount: 0,
      },
    };
  }

  const callsPerToken = 4;
  const tokenBatchSize = Math.max(1, Math.floor(config.batchSize / callsPerToken));
  const tokenBatches = splitIntoChunks(normalizedIds, tokenBatchSize);

  let aggregateCalls = 0;
  let partialFailureCount = 0;

  for (const tokenBatch of tokenBatches) {
    const calls = [];
    const callIndex = [];

    for (const tokenId of tokenBatch) {
      const allowFailure = !config.requireSuccess;
      calls.push({
        target: nftContract.target,
        allowFailure,
        callData: nftInterface.encodeFunctionData("ownerOf", [tokenId]),
      });
      callIndex.push({ tokenId, field: "owner" });

      calls.push({
        target: nftContract.target,
        allowFailure,
        callData: nftInterface.encodeFunctionData("getRetirementDetails", [tokenId]),
      });
      callIndex.push({ tokenId, field: "retirementDetails" });

      calls.push({
        target: marketplaceContract.target,
        allowFailure,
        callData: marketplaceInterface.encodeFunctionData("listings", [tokenId]),
      });
      callIndex.push({ tokenId, field: "listingRaw" });

      calls.push({
        target: marketplaceContract.target,
        allowFailure,
        callData: marketplaceInterface.encodeFunctionData("auctions", [tokenId]),
      });
      callIndex.push({ tokenId, field: "auctionRaw" });
    }

    try {
      const multicall = await multicallRead(provider, calls, {
        multicallAddress: config.address,
        batchSize: calls.length,
      });
      aggregateCalls += multicall.aggregateCalls;

      const perToken = new Map();
      for (const tokenId of tokenBatch) {
        perToken.set(tokenId, {
          owner: null,
          retirementDetails: { isRetired: false, verifier: null, timestamp: null },
          listingRaw: null,
          auctionRaw: null,
          failed: false,
        });
      }

      for (let idx = 0; idx < callIndex.length; idx += 1) {
        const meta = callIndex[idx];
        const result = multicall.results[idx];
        const tokenData = perToken.get(meta.tokenId);
        if (!tokenData) continue;

        if (!result?.success) {
          tokenData.failed = true;
          partialFailureCount += 1;
          continue;
        }

        try {
          if (meta.field === "owner") {
            tokenData.owner = decodeResult(nftInterface, "ownerOf", result.returnData);
          } else if (meta.field === "retirementDetails") {
            tokenData.retirementDetails = decodeResult(nftInterface, "getRetirementDetails", result.returnData);
          } else if (meta.field === "listingRaw") {
            tokenData.listingRaw = decodeResult(marketplaceInterface, "listings", result.returnData);
          } else if (meta.field === "auctionRaw") {
            tokenData.auctionRaw = decodeResult(marketplaceInterface, "auctions", result.returnData);
          }
        } catch {
          tokenData.failed = true;
          partialFailureCount += 1;
        }
      }

      for (const tokenId of tokenBatch) {
        const tokenData = perToken.get(tokenId);
        if (!tokenData || tokenData.failed) {
          failedTokenIds.push(tokenId);
          continue;
        }

        const previous = currentState?.[tokenId] || {};
        const patch = buildDynamicPatchFromReads({
          tokenId,
          owner: tokenData.owner,
          retirementDetails: tokenData.retirementDetails,
          listingRaw: tokenData.listingRaw,
          auctionRaw: tokenData.auctionRaw,
          account: userAccount,
          existingAuctionStartBlock: previous?.auctionData?.auctionStartBlock ?? null,
        });

        patches.push(patch);
      }
    } catch {
      for (const tokenId of tokenBatch) {
        failedTokenIds.push(tokenId);
      }
    }
  }

  return {
    patches,
    failedTokenIds,
    stats: {
      tokenCount: normalizedIds.length,
      lowLevelCalls: normalizedIds.length * callsPerToken,
      aggregateCalls,
      partialFailureCount,
    },
  };
}

export async function hydrateFullTokensViaMulticall({
  provider,
  nftContract,
  marketplaceContract,
  nftInterface,
  marketplaceInterface,
  tokenIds,
  userAccount,
  currentState,
  metadataCache,
  fetchMetadata,
}) {
  const config = getMulticallConfig();
  const normalizedIds = (tokenIds || []).map(normalizeTokenId).filter(Boolean);

  const patches = [];
  const failedTokenIds = [];

  if (!normalizedIds.length) {
    return {
      patches,
      failedTokenIds,
      stats: {
        tokenCount: 0,
        lowLevelCalls: 0,
        aggregateCalls: 0,
        partialFailureCount: 0,
      },
    };
  }

  const callsPerToken = 5;
  const tokenBatchSize = Math.max(1, Math.floor(config.batchSize / callsPerToken));
  const tokenBatches = splitIntoChunks(normalizedIds, tokenBatchSize);

  let aggregateCalls = 0;
  let partialFailureCount = 0;

  for (const tokenBatch of tokenBatches) {
    const calls = [];
    const callIndex = [];

    for (const tokenId of tokenBatch) {
      const allowFailure = !config.requireSuccess;
      calls.push({
        target: nftContract.target,
        allowFailure,
        callData: nftInterface.encodeFunctionData("ownerOf", [tokenId]),
      });
      callIndex.push({ tokenId, field: "owner" });

      calls.push({
        target: nftContract.target,
        allowFailure,
        callData: nftInterface.encodeFunctionData("tokenURI", [tokenId]),
      });
      callIndex.push({ tokenId, field: "tokenURI" });

      calls.push({
        target: nftContract.target,
        allowFailure,
        callData: nftInterface.encodeFunctionData("getRetirementDetails", [tokenId]),
      });
      callIndex.push({ tokenId, field: "retirementDetails" });

      calls.push({
        target: marketplaceContract.target,
        allowFailure,
        callData: marketplaceInterface.encodeFunctionData("listings", [tokenId]),
      });
      callIndex.push({ tokenId, field: "listingRaw" });

      calls.push({
        target: marketplaceContract.target,
        allowFailure,
        callData: marketplaceInterface.encodeFunctionData("auctions", [tokenId]),
      });
      callIndex.push({ tokenId, field: "auctionRaw" });
    }

    try {
      const multicall = await multicallRead(provider, calls, {
        multicallAddress: config.address,
        batchSize: calls.length,
      });
      aggregateCalls += multicall.aggregateCalls;

      const perToken = new Map();
      for (const tokenId of tokenBatch) {
        perToken.set(tokenId, {
          owner: null,
          tokenURI: null,
          retirementDetails: { isRetired: false, verifier: null, timestamp: null },
          listingRaw: null,
          auctionRaw: null,
          failed: false,
        });
      }

      for (let idx = 0; idx < callIndex.length; idx += 1) {
        const meta = callIndex[idx];
        const result = multicall.results[idx];
        const tokenData = perToken.get(meta.tokenId);
        if (!tokenData) continue;

        if (!result?.success) {
          tokenData.failed = true;
          partialFailureCount += 1;
          continue;
        }

        try {
          if (meta.field === "owner") {
            tokenData.owner = decodeResult(nftInterface, "ownerOf", result.returnData);
          } else if (meta.field === "tokenURI") {
            tokenData.tokenURI = decodeResult(nftInterface, "tokenURI", result.returnData);
          } else if (meta.field === "retirementDetails") {
            tokenData.retirementDetails = decodeResult(nftInterface, "getRetirementDetails", result.returnData);
          } else if (meta.field === "listingRaw") {
            tokenData.listingRaw = decodeResult(marketplaceInterface, "listings", result.returnData);
          } else if (meta.field === "auctionRaw") {
            tokenData.auctionRaw = decodeResult(marketplaceInterface, "auctions", result.returnData);
          }
        } catch {
          tokenData.failed = true;
          partialFailureCount += 1;
        }
      }

      for (const tokenId of tokenBatch) {
        const tokenData = perToken.get(tokenId);
        if (!tokenData || tokenData.failed) {
          failedTokenIds.push(tokenId);
          continue;
        }

        const previous = currentState?.[tokenId] || {};
        const dynamicPatch = buildDynamicPatchFromReads({
          tokenId,
          owner: tokenData.owner,
          retirementDetails: tokenData.retirementDetails,
          listingRaw: tokenData.listingRaw,
          auctionRaw: tokenData.auctionRaw,
          account: userAccount,
          existingAuctionStartBlock: previous?.auctionData?.auctionStartBlock ?? null,
        });

        const resolvedTokenURI = tokenData.tokenURI || previous?.tokenURI || previous?.uri || null;

        if (!resolvedTokenURI) {
          patches.push({
            ...dynamicPatch,
            tokenURI: null,
            uri: null,
            ...getFallbackMetadata(tokenId),
            metadataStatus: "missing-uri",
            hiddenFromMarketplace: true,
            fullHydratedAt: Date.now(),
          });
          continue;
        }

        try {
          if (metadataCache?.[resolvedTokenURI]) {
            patches.push({
              ...dynamicPatch,
              tokenURI: resolvedTokenURI,
              uri: resolvedTokenURI,
              ...metadataCache[resolvedTokenURI],
              metadataStatus: "cached",
              hiddenFromMarketplace: false,
              fullHydratedAt: Date.now(),
            });
            continue;
          }

          const fetched = await fetchMetadata?.(resolvedTokenURI, tokenId);
          if (fetched?.metadata) {
            patches.push({
              ...dynamicPatch,
              tokenURI: resolvedTokenURI,
              uri: resolvedTokenURI,
              ...fetched.metadata,
              metadataStatus: fetched.metadataStatus || "loaded",
              hiddenFromMarketplace: false,
              fullHydratedAt: Date.now(),
            });
          } else {
            patches.push({
              ...dynamicPatch,
              tokenURI: resolvedTokenURI,
              uri: resolvedTokenURI,
              ...getFallbackMetadata(tokenId),
              metadataStatus: "fallback",
              hiddenFromMarketplace: false,
              fullHydratedAt: Date.now(),
            });
          }
        } catch {
          patches.push({
            ...dynamicPatch,
            tokenURI: resolvedTokenURI,
            uri: resolvedTokenURI,
            ...getFallbackMetadata(tokenId),
            metadataStatus: "fallback",
            hiddenFromMarketplace: false,
            fullHydratedAt: Date.now(),
          });
        }
      }
    } catch {
      for (const tokenId of tokenBatch) {
        failedTokenIds.push(tokenId);
      }
    }
  }

  return {
    patches,
    failedTokenIds,
    stats: {
      tokenCount: normalizedIds.length,
      lowLevelCalls: normalizedIds.length * callsPerToken,
      aggregateCalls,
      partialFailureCount,
    },
  };
}
